/* eslint-disable react-refresh/only-export-components */
import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
  type ReactNode,
} from 'react';
import type { ThemePreference } from '../types';
import { usersApi } from '../api';
import { useAuth } from './AuthContext';
import {
  readStoredTheme, writeStoredTheme, resolveTheme, type ResolvedTheme,
} from '../data/theme';

interface ThemeContextType {
  // The user's chosen mode (what the toggle reflects).
  mode: ThemePreference;
  // The concrete theme on the DOM ('system' collapsed to light/dark).
  resolved: ResolvedTheme;
  setMode: (mode: ThemePreference) => void;
}

// A sensible default so consumers (notably the ThemeToggle embedded in shared
// nav chrome) render harmlessly even when no provider is mounted — e.g. in
// isolated component tests. The real app always wraps in <ThemeProvider>.
const ThemeContext = createContext<ThemeContextType>({
  mode: 'system',
  resolved: 'light',
  setMode: () => {},
});

const THEME_COLOR: Record<ResolvedTheme, string> = {
  light: '#0A6E7E',
  dark: '#0f1518',
};

// Reflect the resolved theme onto <html data-theme> (which drives every CSS var)
// and the address-bar meta. Mirrors the pre-paint script in index.html.
function applyTheme(resolved: ResolvedTheme): void {
  document.documentElement.dataset.theme = resolved;
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', THEME_COLOR[resolved]);
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [mode, setModeState] = useState<ThemePreference>(() => readStoredTheme());
  const [resolved, setResolved] = useState<ResolvedTheme>(() =>
    resolveTheme(readStoredTheme()),
  );

  // Apply the active mode (cached locally, then reconciled to the account once
  // signed in) to <html>. This holds even when logged out, so a returning user's
  // login / password-reset screens keep the mode they last chose on this device;
  // a fresh visitor with no saved preference gets light (see readStoredTheme).
  // Pages that must never go dark (marketing, privacy, terms, register) pin their
  // own subtree to light with `data-theme="light"`.
  useEffect(() => {
    const next = resolveTheme(mode);
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setResolved(next);
    applyTheme(next);
    if (mode !== 'system' || typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (e: MediaQueryListEvent) => {
      const r: ResolvedTheme = e.matches ? 'dark' : 'light';
      setResolved(r);
      applyTheme(r);
    };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [mode]);

  // The account's stored preference is the cross-device source of truth. Adopt
  // it once per signed-in user, when it differs from the local cache (e.g. the
  // user set dark on another device). Guarded by a ref so it runs once per user
  // and never fights a change the user just made here.
  const reconciledUserId = useRef<string | null>(null);
  useEffect(() => {
    if (!user) {
      reconciledUserId.current = null;
      return;
    }
    if (reconciledUserId.current === user.id) return;
    reconciledUserId.current = user.id;
    if (user.themePreference !== mode) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setModeState(user.themePreference);
      writeStoredTheme(user.themePreference);
    }
  }, [user, mode]);

  // A user-driven change: apply locally (instant) and best-effort sync to the
  // account so it follows them to another device. A failed sync never blocks the
  // toggle — the local change + cache still apply (mirrors `changeView`).
  const setMode = useCallback((next: ThemePreference) => {
    setModeState(next);
    writeStoredTheme(next);
    void usersApi.updatePrefs({ themePreference: next }).catch(() => {
      /* preference still applies locally; cross-device sync just lagged */
    });
  }, []);

  const value = useMemo<ThemeContextType>(
    () => ({ mode, resolved, setMode }),
    [mode, resolved, setMode],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  return useContext(ThemeContext);
}
